import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { Trophy, Users, Zap, Lock, ChevronRight, Copy, Coffee } from "lucide-react";
import { InstallPWAButton } from "@/components/InstallPWAButton";
import { AppPreviewMockups } from "@/components/AppPreviewMockups";
import { BulkTipsDemo } from "@/components/BulkTipsDemo";
import { AvailableTournaments } from "@/components/AvailableTournaments";
import { Testimonials } from "@/components/Testimonials";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/ads/AdSlot";

export default function Landing() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const canonicalUrl = `https://tipomatic.app${pathname}`;

  const features = [
    { icon: Users, title: t("landing.features.f1t"), body: t("landing.features.f1b") },
    { icon: Zap, title: t("landing.features.f2t"), body: t("landing.features.f2b") },
    { icon: Trophy, title: t("landing.features.f3t"), body: t("landing.features.f3b") },
    { icon: Lock, title: t("landing.features.f4t"), body: t("landing.features.f4b") },
    { icon: Copy, title: t("landing.features.f5t"), body: t("landing.features.f5b") },
  ];

  const steps = [
    { n: "01", t: t("landing.steps.s1t"), d: t("landing.steps.s1d") },
    { n: "02", t: t("landing.steps.s2t"), d: t("landing.steps.s2d") },
    { n: "03", t: t("landing.steps.s3t"), d: t("landing.steps.s3d") },
    { n: "04", t: t("landing.steps.s4t"), d: t("landing.steps.s4d") },
  ];

  const faqs = [
    { q: t("landing.faq.q1"), a: t("landing.faq.a1") },
    { q: t("landing.faq.q2"), a: t("landing.faq.a2") },
    { q: t("landing.faq.q3"), a: t("landing.faq.a3") },
    { q: t("landing.faq.q4"), a: t("landing.faq.a4") },
  ];

  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <Helmet>
        <title>Tipomatic — {t("auth.tagline")}</title>
        <meta name="description" content={t("landing.hero.subtitle")} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="cs" href="https://tipomatic.app/cs" />
        <link rel="alternate" hrefLang="en" href="https://tipomatic.app/en" />
        <link rel="alternate" hrefLang="x-default" href="https://tipomatic.app/" />
        <meta property="og:title" content={`Tipomatic — ${t("auth.tagline")}`} />
        <meta property="og:description" content={t("landing.hero.subtitle")} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tipomatic" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="container mx-auto px-4 py-5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-bold text-base sm:text-lg min-w-0">
          <img src="/icon-512.png?v=2" alt="Tipomatic logo" width="32" height="32" decoding="async" className="h-8 w-8 rounded-lg object-cover shrink-0" {...({ fetchpriority: "high" } as any)} />
          <span className="truncate">Tipomatic</span>
        </div>
        <nav className="flex items-center gap-1 sm:gap-2 shrink-0">
          <LanguageSwitcher />
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="hidden sm:inline-flex text-pitch-cream hover:bg-white/10 hover:text-pitch-cream"
          >
            <Link to="/auth">{t("landing.nav.signIn")}</Link>
          </Button>
          <Button
            asChild
            size="sm"
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-3 sm:px-4"
          >
            <Link to="/auth">{t("landing.nav.signUp")}</Link>
          </Button>
        </nav>
      </header>

      <main>


      {/* Hero */}
      <section className="container mx-auto px-4 pt-12 pb-24 text-center max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-pitch text-xs uppercase tracking-widest text-primary mb-6">
          {t("landing.hero.badge")}
        </div>
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
          {t("landing.hero.title1")}
          <br />
          <span className="text-primary">{t("landing.hero.title2")}</span>
        </h1>
        <p className="text-lg md:text-xl text-pitch-cream/70 mb-10 max-w-xl mx-auto">{t("landing.hero.subtitle")}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            asChild
            size="lg"
            className="font-semibold text-base h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Link to="/auth">
              {t("landing.hero.cta")} <ChevronRight className="ml-1 h-5 w-5" />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="h-14 px-8 border-pitch text-pitch-cream hover:bg-white/5 hover:text-pitch-cream bg-transparent"
          >
            <a href="#jak-to-funguje">{t("landing.hero.how")}</a>
          </Button>
          <InstallPWAButton
            size="lg"
            className="h-14 px-8 border-pitch text-pitch-cream hover:bg-white/5 hover:text-pitch-cream bg-transparent"
          />
        </div>
        <p className="mt-5 text-sm text-pitch-cream/50">{t("landing.hero.microcopy")}</p>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {features.map((f) => (
            <div key={f.title} className="border border-pitch rounded-2xl p-6 bg-white/[0.02] backdrop-blur">
              <f.icon className="h-7 w-7 text-primary mb-3" />
              <h2 className="font-semibold text-lg mb-1">{f.title}</h2>
              <p className="text-pitch-cream/65 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
        <BulkTipsDemo />
      </section>

      <div className="container mx-auto px-4 max-w-3xl">
        <AdSlot placement="landing-inline" seed="landing-top" />
      </div>

      <div className="relative">

        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-1/4 -z-0 mx-auto h-[420px] max-w-3xl rounded-full bg-primary/15 blur-[140px]"
        />
        <AppPreviewMockups />
      </div>

      <AvailableTournaments />

      <Testimonials />

      {/* Steps */}
      <section id="jak-to-funguje" className="bg-gradient-pitch border-y border-pitch py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">
            {t("landing.steps.title")} <span className="text-primary">{t("landing.steps.title2")}</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="text-left">
                <div className="text-primary font-mono text-sm mb-2">{s.n}</div>
                <h3 className="font-semibold text-lg mb-1">{s.t}</h3>
                <p className="text-pitch-cream/60 text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-24 max-w-2xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
          {t("landing.faq.title")} <span className="text-primary">{t("landing.faq.title2")}</span>
        </h2>
        <div className="space-y-4">
          {faqs.map((f) => (
            <details
              key={f.q}
              className="group border border-pitch rounded-xl p-5 bg-white/[0.02] [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex items-center justify-between cursor-pointer font-semibold">
                {f.q}
                <ChevronRight className="h-5 w-5 transition-transform group-open:rotate-90 text-primary" />
              </summary>
              <p className="mt-3 text-pitch-cream/65 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-6 rounded-xl border border-pitch bg-white/[0.02] px-5 py-6 text-center space-y-3">
          <p className="text-sm text-pitch-cream/65">{t("help.bmcBody")}</p>
          <a
            href="https://www.buymeacoffee.com/incubator"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#FFDD00] text-black px-5 py-2.5 text-sm font-semibold hover:brightness-110 transition"
          >
            <Coffee className="h-4 w-4" />
            {t("help.bmcButton")}
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 pb-12 max-w-3xl">
        <AdSlot placement="landing-inline" seed="landing-bottom" />
      </section>

      <section className="container mx-auto px-4 pb-24">
        <div className="rounded-3xl border border-pitch bg-white/[0.03] p-12 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("landing.cta.title")}</h2>
          <p className="text-pitch-cream/70 mb-8">{t("landing.cta.subtitle")}</p>
          <Button
            asChild
            size="lg"
            className="font-semibold h-14 px-10 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Link to="/auth">{t("landing.cta.button")}</Link>
          </Button>
        </div>
      </section>

      </main>

      <Footer variant="dark" />
    </div>
  );
}
